import React, { useState } from 'react';
import { 
  Server, ShieldAlert, Activity, Database, Cloud, RefreshCcw, Lock, HardDrive, Cpu, 
  GitCommit, Clock, CheckCircle2, XCircle, AlertTriangle, ArrowRight, DollarSign, 
  TrendingUp, TrendingDown, Terminal, History, Globe
} from 'lucide-react';
import { MOCK_DEPLOYMENTS, MOCK_BACKUPS, MOCK_SLOS, MOCK_COSTS, INFRA_HEALTH } from '../../data/devopsData';

type Tab = 'executive' | 'deployments' | 'database' | 'sre' | 'dr' | 'finops';

export function ProductionCommandCenter({ onNavigateToView }: { onNavigateToView: (v: string) => void }) {
  const [activeTab, setActiveTab] = useState<Tab>('executive');

  const renderTabs = () => (
    <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E2E8F0] pb-4">
      {[
        { id: 'executive', label: 'Executive Overview', icon: Globe },
        { id: 'deployments', label: 'CI/CD & Deployments', icon: GitCommit },
        { id: 'database', label: 'PostgreSQL & State', icon: Database },
        { id: 'sre', label: 'SRE & Observability', icon: Activity },
        { id: 'dr', label: 'Backups & DR', icon: ShieldAlert },
        { id: 'finops', label: 'FinOps & Costs', icon: DollarSign },
      ].map((tab) => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as Tab)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
              isActive 
                ? 'bg-[#1E40AF] text-white' 
                : 'bg-white text-[#475569] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
            }`}
          >
            <Icon className="w-4 h-4" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24">
      <div className="bg-[#0F172A] text-white pt-16 pb-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#1E40AF] rounded-full blur-[120px] opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-3 text-[#94A3B8] mb-4 text-sm font-medium">
            <Server className="w-4 h-4" />
            <span>YamooPay Platform Engineering</span>
            <span>/</span>
            <span className="text-white">Production Command Center</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-white">
            Global Production & SRE
          </h1>
          <p className="text-lg text-[#CBD5E1] max-w-3xl leading-relaxed">
            Infrastructure as Code, CI/CD, PostgreSQL Governance, Disaster Recovery, and Cost Optimization parameters.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-20">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 mb-8">
          {renderTabs()}

          {activeTab === 'executive' && <ExecutiveOverview />}
          {activeTab === 'deployments' && <DeploymentsOverview />}
          {activeTab === 'database' && <DatabaseOverview />}
          {activeTab === 'sre' && <SREOverview />}
          {activeTab === 'dr' && <DisasterRecoveryOverview />}
          {activeTab === 'finops' && <FinOpsOverview />}
        </div>
      </div>
    </div>
  );
}

function ExecutiveOverview() {
  return (
    <div className="space-y-8">
      {/* Critical HA Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 flex items-start gap-4">
        <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-1" />
        <div>
          <h3 className="text-amber-900 font-bold text-lg mb-2">SINGLE NODE ARCHITECTURE — NOT HIGHLY AVAILABLE</h3>
          <p className="text-amber-800 text-sm leading-relaxed mb-4">
            YamooPay is currently deployed on a single Hostinger VPS constraint (P0 Stage). A complete host failure will result in downtime. High Availability (HA) requires Stage 3 multi-node deployment. Data integrity is preserved via offsite backups and WAL archiving.
          </p>
          <div className="flex flex-wrap gap-2">
            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded">Hostinger VPS SPOF</span>
            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded">PostgreSQL SPOF</span>
            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded">Dokploy Control Plane SPOF</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
          <div className="text-sm font-bold text-[#64748B] mb-1">Global Uptime (30d)</div>
          <div className="text-2xl font-black text-[#0F172A]">99.95%</div>
        </div>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
          <div className="text-sm font-bold text-[#64748B] mb-1">Target RTO</div>
          <div className="text-2xl font-black text-[#0F172A]">&lt; 4 Hours</div>
        </div>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
          <div className="text-sm font-bold text-[#64748B] mb-1">Target RPO</div>
          <div className="text-2xl font-black text-[#0F172A]">&lt; 5 Minutes</div>
        </div>
        <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
          <div className="text-sm font-bold text-[#64748B] mb-1">Current Infra Run Rate</div>
          <div className="text-2xl font-black text-[#0F172A]">$612 / mo</div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-bold text-[#0F172A] mb-4">Resource Saturation (Hostinger VPS)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ResourceGauge label="CPU Utilization" used={INFRA_HEALTH.vps.cpuUsagePct} total={100} unit="%" />
          <ResourceGauge label="Memory (RAM)" used={INFRA_HEALTH.vps.memoryUsageGb} total={INFRA_HEALTH.vps.memoryTotalGb} unit="GB" />
          <ResourceGauge label="Disk Storage" used={INFRA_HEALTH.vps.diskUsageGb} total={INFRA_HEALTH.vps.diskTotalGb} unit="GB" />
        </div>
      </div>
    </div>
  );
}

function ResourceGauge({ label, used, total, unit }: { label: string, used: number, total: number, unit: string }) {
  const pct = Math.min(100, Math.round((used / total) * 100));
  const color = pct > 85 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : 'bg-emerald-500';
  
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
      <div className="flex justify-between items-end mb-2">
        <span className="text-sm font-bold text-[#475569]">{label}</span>
        <span className="text-lg font-black text-[#0F172A]">{used} / {total} {unit}</span>
      </div>
      <div className="w-full bg-[#F1F5F9] rounded-full h-3 overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-1000`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function DeploymentsOverview() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-[#0F172A]">Recent CI/CD Deployments</h3>
        <span className="px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-full border border-emerald-200">
          AutoDeploy: ENABLED
        </span>
      </div>
      <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr>
              <th className="px-4 py-3 font-bold text-[#475569]">Version</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Commit</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Date</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Duration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {MOCK_DEPLOYMENTS.map(d => (
              <tr key={d.id} className="hover:bg-[#F8FAFC]">
                <td className="px-4 py-3 font-bold text-[#0F172A]">{d.version}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{d.commitSha}</td>
                <td className="px-4 py-3 text-[#475569]">{new Date(d.deployedAt).toLocaleString()}</td>
                <td className="px-4 py-3">
                  {d.status === 'SUCCESS' ? (
                    <span className="flex items-center gap-1 text-emerald-600 font-bold text-xs"><CheckCircle2 className="w-3 h-3"/> SUCCESS</span>
                  ) : d.status === 'ROLLBACK' ? (
                    <span className="flex items-center gap-1 text-amber-600 font-bold text-xs"><RefreshCcw className="w-3 h-3"/> ROLLBACK</span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-600 font-bold text-xs"><XCircle className="w-3 h-3"/> FAILED</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[#475569]">{Math.round(d.durationMs / 1000)}s</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-xl text-sm text-[#475569]">
        <strong>CI Pipeline Gates:</strong> Linter &rarr; Typecheck &rarr; Financial Golden Tests &rarr; TruffleHog Secret Scan &rarr; Multi-Stage Docker Build &rarr; Dokploy Webhook.
      </div>
    </div>
  );
}

function DatabaseOverview() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-[#E2E8F0] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Database className="w-5 h-5 text-[#1E40AF]" />
            <h3 className="font-bold text-[#0F172A] text-lg">PostgreSQL Primary</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-[#64748B]">Version</span><span className="font-bold">{INFRA_HEALTH.postgres.version}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Connections</span><span className="font-bold">{INFRA_HEALTH.postgres.connections} / {INFRA_HEALTH.postgres.maxConnections}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Disk Usage</span><span className="font-bold">{INFRA_HEALTH.postgres.diskUsageGb} GB</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">CPU Usage</span><span className="font-bold">{INFRA_HEALTH.postgres.cpuUsagePct}%</span></div>
          </div>
        </div>
        <div className="border border-[#E2E8F0] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Server className="w-5 h-5 text-red-500" />
            <h3 className="font-bold text-[#0F172A] text-lg">Redis (Cache & Queue)</h3>
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-[#64748B]">Version</span><span className="font-bold">{INFRA_HEALTH.redis.version}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Memory</span><span className="font-bold">{INFRA_HEALTH.redis.memoryUsageMb} MB / {INFRA_HEALTH.redis.memoryTotalMb} MB</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Clients</span><span className="font-bold">{INFRA_HEALTH.redis.connectedClients}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Persistence</span><span className="font-bold text-emerald-600">AOF + RDB</span></div>
          </div>
        </div>
      </div>
      
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-xl text-sm text-[#475569]">
        <strong>Data Governance Rule:</strong> PostgreSQL is the absolute system of record. Redis is strictly treated as ephemeral for queues and rate-limiting. Redis data loss must not result in financial ledger corruption.
      </div>
    </div>
  );
}

function SREOverview() {
  return (
    <div className="space-y-6">
      <h3 className="text-lg font-bold text-[#0F172A] mb-2">Service Level Objectives (SLOs)</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {MOCK_SLOS.map(slo => (
          <div key={slo.id} className="border border-[#E2E8F0] rounded-xl p-4 bg-white">
            <div className="flex justify-between items-start mb-2">
              <span className="font-bold text-[#0F172A]">{slo.name}</span>
              {slo.status === 'HEALTHY' ? (
                <span className="px-2 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded uppercase">Healthy</span>
              ) : slo.status === 'AT_RISK' ? (
                <span className="px-2 py-1 bg-amber-50 text-amber-700 text-[10px] font-bold rounded uppercase">At Risk</span>
              ) : (
                <span className="px-2 py-1 bg-red-50 text-red-700 text-[10px] font-bold rounded uppercase">Violated</span>
              )}
            </div>
            <div className="flex items-end gap-2">
              <span className={`text-2xl font-black ${slo.status === 'VIOLATED' ? 'text-red-600' : 'text-[#0F172A]'}`}>
                {slo.actualPct}%
              </span>
              <span className="text-xs font-bold text-[#64748B] mb-1">/ Target {slo.targetPct}%</span>
            </div>
          </div>
        ))}
      </div>
      <div className="bg-[#F8FAFC] border border-[#E2E8F0] p-4 rounded-xl text-sm text-[#475569]">
        <strong>OpenTelemetry Pipeline:</strong> Node.js SDK &rarr; OTel Collector &rarr; Prometheus (Metrics) & Loki (Logs) & Tempo (Traces). Sensitive financial data (e.g. raw salaries) is redacted at the Collector level.
      </div>
    </div>
  );
}

function DisasterRecoveryOverview() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-bold text-[#0F172A]">Disaster Recovery & Backups</h3>
        <p className="text-sm text-[#64748B]">Backups are stored offsite (Backblaze B2). WAL Archiving enabled for Point-in-Time Recovery (PITR).</p>
      </div>

      <div className="border border-[#E2E8F0] rounded-xl overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
            <tr>
              <th className="px-4 py-3 font-bold text-[#475569]">Type</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Completed At</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Size</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Offsite / Encrypted</th>
              <th className="px-4 py-3 font-bold text-[#475569]">Restore Tested</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E2E8F0]">
            {MOCK_BACKUPS.map(b => (
              <tr key={b.id} className="hover:bg-[#F8FAFC]">
                <td className="px-4 py-3 font-bold text-[#0F172A]">{b.type}</td>
                <td className="px-4 py-3 text-[#475569]">{new Date(b.completedAt).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#0F172A]">{b.sizeMb} MB</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    {b.isOffsite && <Cloud className="w-4 h-4 text-emerald-600" title="Offsite Verified" />}
                    {b.encryption && <Lock className="w-4 h-4 text-emerald-600" title="AES-256 Encrypted" />}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {b.restoredTested ? (
                    <span className="flex items-center gap-1 text-emerald-600 font-bold text-xs"><CheckCircle2 className="w-3 h-3"/> VERIFIED</span>
                  ) : (
                    <span className="flex items-center gap-1 text-[#94A3B8] font-bold text-xs">UNTESTED</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl text-sm text-amber-900">
        <strong>Rule 592 - BACKUP ≠ DISASTER RECOVERY:</strong> A backup is only valid if a restore has been successfully tested. Offsite logical dumps are automated daily, physical base backups weekly.
      </div>
    </div>
  );
}

function FinOpsOverview() {
  const totalMonthly = MOCK_COSTS.reduce((sum, cost) => sum + cost.estimatedMonthlyUsd, 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-[#0F172A]">FinOps: Infrastructure & API Cost</h3>
        <div className="text-right">
          <div className="text-xs font-bold text-[#64748B]">Projected Monthly Spend</div>
          <div className="text-3xl font-black text-[#0F172A]">${totalMonthly.toFixed(2)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {MOCK_COSTS.map(cost => (
          <div key={cost.id} className="border border-[#E2E8F0] rounded-xl p-4 flex justify-between items-center">
            <div>
              <div className="font-bold text-[#0F172A] text-sm">{cost.name}</div>
              {cost.trendPct > 0 ? (
                <div className="flex items-center gap-1 text-amber-600 text-[10px] font-bold mt-1">
                  <TrendingUp className="w-3 h-3" /> +{cost.trendPct}% MoM
                </div>
              ) : cost.trendPct < 0 ? (
                <div className="flex items-center gap-1 text-emerald-600 text-[10px] font-bold mt-1">
                  <TrendingDown className="w-3 h-3" /> {cost.trendPct}% MoM
                </div>
              ) : (
                <div className="text-[#94A3B8] text-[10px] font-bold mt-1">Stable</div>
              )}
            </div>
            <div className="font-mono text-lg font-bold text-[#0F172A]">${cost.estimatedMonthlyUsd.toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
